import React, { useMemo, useState, useEffect, useReducer, useCallback, useRef } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "react-table-7";
import { Table, Spinner, Row, Col } from "react-bootstrap";
import PaginationComponent from "./PaginationComponent";
import "./style.css";
import {
  columnsContent,
  useReactTableHooks,
  renderNoDataComponent,
  renderTableHeaderContent,
  renderTableRowsContent,
  getRowId,
} from "./commonReactTableFunctions";

const SwimSwitchServerSideReactTable = ({
  columnDefination,
  data = [],
  currentPage,
  totalPages,
  totalRecords,
  defaultPageSize,
  attributeName,
  onUpdateTableData,
  loading,
  enableMultiRowSelection = true,
  addRowSelection = false,
  overrideRowId = null,
  onRowSelect,
  filters,
  onRowClick,
  sorted,
  CustomPaginationComponent,
  extraPropsForCustomComponent,
  customPagination = true,
  customFilters = true,
  customSorting = true,
  enableMultiSort = true,
  enableExpansion = false,
  toggleAllRowsSelect = null,
  enableAllRowSelect = true,
  enableRowSelection = true,
  globalSelected = null,
  pageSizeOptions = [5, 10, 15, 20, 30, 50],
  resetTableSelection = false,
  columnShow = Object.fromEntries(
    columnDefination.map((column) => [
      column.accessor,
      column.show === undefined || column.show ? true : column.show,
    ])
  ),
  trStyle = (row) => {
    return {};
  },
  tdStyle = (row, column) => {
    return {};
  },
  clicked = [],
  dataTestId,
  shouldFetchOnEmptyFilters = true, // Prop from Redux/parent
}) => {
  // --- Unified Table State for Data Fetching ---
  // This single state object will hold all parameters that trigger a data fetch.
  // Initialize from props.
    const [toggleAllRowsSelectValue, setToggleAllRowsSelect] =
    useState(toggleAllRowsSelect);
  const [tableFetchParams, setTableFetchParams] = useState({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
    columnFilters: filters,
    sorting: sorted,
  });

  // Ref to store the actual parameters that were *last used* for a successful API call.
  // This helps prevent redundant fetches if dependencies change but the logical parameters haven't.
  const prevFetchedParamsRef = useRef({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
    columnFilters: filters,
    sorting: sorted,
  });

  // Debounce ref for API calls
  const debounceTimeoutRef = useRef(null);

  // --- Other Local States (not directly triggering API calls, but controlled by react-table-7) ---
  const [selectedRowIds, setSelectedRows] = useState({});
  const [columnVisibility, setColumnVisibility] = useReducer(
    columnVisibilityReducer,
    columnShow
  );
  const [expanded, setExpanded] = useState({});

  function columnVisibilityReducer(state, action) {
    let newState = {};
    newState = Object.assign(newState, action);
    return newState;
  }

  const handleRowClick = useCallback((row, cell) => {
    row.column = cell.column;
    if (
      onRowClick &&
      cell.column.id !== "select" &&
      cell.column.id !== "option"
    ) {
      onRowClick(row);
    }
  }, [onRowClick]);


  const getRowKey = useCallback((row, index) => {
    return getRowId(row, index, overrideRowId);
  }, [overrideRowId]);

  const columns = useMemo(
    () =>
      columnsContent(
        columnDefination,
        addRowSelection,
        enableExpansion,
        enableAllRowSelect
      ),
    [columnDefination, addRowSelection, enableExpansion, enableAllRowSelect]
  );

  const paginationState = useMemo(
    () => ({
      pageIndex: tableFetchParams.pageIndex, // Derive from unified state
      pageSize: tableFetchParams.pageSize,   // Derive from unified state
    }),
    [tableFetchParams.pageIndex, tableFetchParams.pageSize]
  );

  // --- React Table Instance ---
  const tableConfig = {
    columns,
    data: data || [],
    initialState: {
      columnFilters: [],
      sorting: [],
      selectedRowIds: {},
      pagination: {
        pageSize: defaultPageSize,
      },
    },
    state: {
      rowSelection: selectedRowIds,
      sorting: tableFetchParams.sorting, // Derive from unified state
      columnFilters: tableFetchParams.columnFilters, // Derive from unified state
      pagination: paginationState,
      columnVisibility: columnVisibility,
      expanded,
    },
    pageCount: totalPages,
    enableRowSelection: enableRowSelection,
    onRowSelectionChange: setSelectedRows,
    enableColumnResizing: true,
    enableMultiRowSelection: enableMultiRowSelection,
    enableMultiSort: true,
    manualFiltering: customFilters,
    manualPagination: customPagination,
    onColumnFiltersChange: (updaterOrValue) => {
      setTableFetchParams(prev => {
        const newFilters = typeof updaterOrValue === 'function' ? updaterOrValue(prev.columnFilters) : updaterOrValue;
        return {
          ...prev,
          columnFilters: newFilters,
          pageIndex: 0, // Reset page to 0 on filter change
        };
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: (updaterOrValue) => {
      setTableFetchParams(prev => {
        const newPagination = typeof updaterOrValue === 'function' ? updaterOrValue({ pageIndex: prev.pageIndex, pageSize: prev.pageSize }) : updaterOrValue;
        return {
          ...prev,
          pageIndex: newPagination.pageIndex,
          pageSize: newPagination.pageSize,
        };
      });
    },
    onExpandedChange: setExpanded,
    getSubRows: enableExpansion ? (originalRow) => originalRow.subRows : null,
    getRowId: getRowKey,
  };

  if (customSorting) {
    tableConfig.manualSorting = true;
    tableConfig.onSortingChange = (updaterOrValue) => {
      setTableFetchParams(prev => {
        const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(prev.sorting) : updaterOrValue;
        return {
          ...prev,
          sorting: newSorting,
          pageIndex: 0, // Reset page to 0 on sort change
        };
      });
    };
  }

  const table = useReactTable(tableConfig, useReactTableHooks);

  // --- Effect to synchronize incoming props to unified local state ---
  // This runs only when the *props* change, to reflect external control (Redux updates).
  useEffect(() => {
    // Deep compare to prevent unnecessary updates if reference changes but value is same
    const currentProps = {
      currentPage,
      filters,
      sorted,
    };
    const currentLocalState = {
      pageIndex: tableFetchParams.pageIndex,
      columnFilters: tableFetchParams.columnFilters,
      sorting: tableFetchParams.sorting,
    };

    let needsUpdate = false;

    if (currentProps.currentPage !== currentLocalState.pageIndex) {
        needsUpdate = true;
    }
    if (JSON.stringify(currentProps.filters) !== JSON.stringify(currentLocalState.columnFilters)) {
        needsUpdate = true;
    }
    if (JSON.stringify(currentProps.sorted) !== JSON.stringify(currentLocalState.sorting)) {
      if(currentProps.sorted.length == 1 && currentLocalState.sorting.length == 1) {
        if(currentProps.sorted[0].id == currentLocalState.sorting[0].id && currentProps.sorted[0].desc == currentLocalState.sorting[0].desc) {
          // "only diff is in isDefault field no need to update
        } else {
          // diff id or desc, need update
          needsUpdate = true;
        }
      } else {
        // Sort columns added or removed
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
        setTableFetchParams(prev => ({
          ...prev,
          pageIndex: currentProps.currentPage,
          columnFilters: currentProps.filters,
          sorting: currentProps.sorted,
        }));
    }
  }, [currentPage, filters, sorted]); // Depend on all incoming props

  // --- MAIN DATA FETCHING EFFECT (with Debouncing and Conditional Fetch) ---
  useEffect(() => {
    // Clear any existing debounce timer
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    const currentFiltersStr = JSON.stringify(tableFetchParams.columnFilters);
    const currentSortingStr = JSON.stringify(tableFetchParams.sorting);

    // Check if filters are logically empty (accounting for priorityAgent:false if it's a default, non-search filter)
    const areFiltersEmpty =
      tableFetchParams.columnFilters.length === 0 ||
      tableFetchParams.columnFilters.every(f => {
        return (typeof f.value === 'string' && f.value.trim() === '') ||
               (Array.isArray(f.value) && f.value.length === 0) ||
               (f.id === 'priorityAgent' && f.value === false); // Adjust for your specific default filter needs
      });

    // Determine if a fetch is needed based on logical change OR if it's the initial empty state fetch
    const shouldFetch =
      // Has parameters logically changed since last *successful* fetch?
      (tableFetchParams.pageIndex !== prevFetchedParamsRef.current.pageIndex ||
      tableFetchParams.pageSize !== prevFetchedParamsRef.current.pageSize ||
      currentFiltersStr !== JSON.stringify(prevFetchedParamsRef.current.columnFilters) ||
      currentSortingStr !== JSON.stringify(prevFetchedParamsRef.current.sorting)) &&
      // AND should we fetch if filters are empty?
      (shouldFetchOnEmptyFilters || !areFiltersEmpty);


    if (shouldFetch) {
        debounceTimeoutRef.current = setTimeout(() => {
            onUpdateTableData(
                tableFetchParams.columnFilters,
                tableFetchParams.sorting,
                tableFetchParams.pageSize,
                tableFetchParams.pageIndex
            );
            // Update the ref with the parameters that just caused a fetch
            prevFetchedParamsRef.current = {
                pageIndex: tableFetchParams.pageIndex,
                pageSize: tableFetchParams.pageSize,
                columnFilters: tableFetchParams.columnFilters,
                sorting: tableFetchParams.sorting,
            };
        }, 300); // Debounce by 300ms to consolidate rapid state changes
    } else {
        // If should not fetch, reset prevFetchedParamsRef to current tableFetchParams
        // This ensures the next change correctly triggers a fetch if conditions are met
         prevFetchedParamsRef.current = {
            pageIndex: tableFetchParams.pageIndex,
            pageSize: tableFetchParams.pageSize,
            columnFilters: tableFetchParams.columnFilters,
            sorting: tableFetchParams.sorting,
        };
    }

    // Cleanup: Clear timeout on unmount or if dependencies change before timeout
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [
    tableFetchParams.pageIndex,
    tableFetchParams.pageSize,
    tableFetchParams.columnFilters,
    tableFetchParams.sorting,
    shouldFetchOnEmptyFilters, // Added shouldFetchOnEmptyFilters as a dependency
    onUpdateTableData // Stable callback from parent
  ]);


  // --- Other Effects (reviewed for dependencies) ---

  useEffect(() => {
    if (addRowSelection && onRowSelect) {
      onRowSelect(selectedRowIds);
    }
  }, [selectedRowIds, addRowSelection]);

  const toggleColumnVisibility = useCallback(() => {
    table.getAllLeafColumns().forEach((column) => {
      const colDef = columnDefination.find(def => def.accessor === column.id);
      if (colDef) {
        column.toggleVisibility(colDef.show === undefined ? true : colDef.show);
      }
    });
  }, [table, columnDefination]);

  useEffect(() => {
    toggleColumnVisibility();
  }, [columnVisibility, toggleColumnVisibility]);

  useEffect(() => {
    if (JSON.stringify(columnShow) !== JSON.stringify(columnVisibility)) {
      setColumnVisibility(columnShow);
    }
  }, [columnShow, columnVisibility]);

    useEffect(() => {
    if (toggleAllRowsSelect !== toggleAllRowsSelectValue) {
      setToggleAllRowsSelect(toggleAllRowsSelect);
    }
  }, [toggleAllRowsSelect]);

  useEffect(() => {
    if (toggleAllRowsSelectValue) {
      if (globalSelected) {
        let selection = {};
        globalSelected.forEach((id, i) => (selection[id] = true));
        setSelectedRows(selection);
      }
    } else {
      table.resetRowSelection(true);
    }
  }, [toggleAllRowsSelectValue]);

  useEffect(() => {
    if (resetTableSelection) {
      table.resetRowSelection(true);
    }
  }, [resetTableSelection, table]);


  const renderLoader = () => {
    if (loading) {
      return (
        <div className={"overlay"}>
          <Spinner
            animation="border"
            variant="primary"
            className={"table-spinner"}
          />
        </div>
      );
    }
    return null;
  };

  const renderTableHeader = useMemo(
    () => renderTableHeaderContent(table.getHeaderGroups()),
    [
      table,
      tableFetchParams.columnFilters, // Use unified state
      selectedRowIds,
      data,
      tableFetchParams.pageIndex, // Use unified state
      tableFetchParams.pageSize, // Use unified state
      clicked,
      tableFetchParams.sorting, // Use unified state
    ]
  );

  const renderTableRows = useMemo(
    () =>
      renderTableRowsContent(
        table.getRowModel().rows,
        handleRowClick,
        trStyle,
        tdStyle
      ),
    [
      table,
      selectedRowIds,
      data,
      tableFetchParams.pageIndex, // Use unified state
      tableFetchParams.pageSize, // Use unified state
      clicked,
      tableFetchParams.sorting, // Use unified state
      handleRowClick
    ]
  );

  return (
    <Row>
      <Col>
        <div className={'table-horizontal-scroll'}>
          <Table bordered>
            <thead data-testid={`thead-${dataTestId}`}>
              {renderTableHeader}
            </thead>
            <tbody data-testid={`tbody-${dataTestId}`}>
              <>
                {renderTableRows}
                {renderNoDataComponent(
                  table.getRowModel().rows.length,
                  columns.length
                )}
              </>
            </tbody>
          </Table>
        </div>
        {CustomPaginationComponent ? (
          React.cloneElement(
            CustomPaginationComponent, {
              callGroupData: extraPropsForCustomComponent,
              currentPage: tableFetchParams.pageIndex, // Use unified state
              totalPages: table.getPageCount(),
              pageSizeOptions: pageSizeOptions,
              selectedPageSize: tableFetchParams.pageSize, // Use unified state
              attributeName: attributeName,
              onChangePage: (pageNo) => {
                // Update unified state
                setTableFetchParams(prev => ({ ...prev, pageIndex: pageNo }));
              },
              onChangePageSize: (e) => {
                // Update unified state
                setTableFetchParams(prev => ({ ...prev, pageIndex: 0, pageSize: e }));
              },
              totalRecords: totalRecords,
            }
          )
        ) : (
          <PaginationComponent
            currentPage={tableFetchParams.pageIndex} // Use unified state
            totalPages={table.getPageCount()}
            pageSizeOptions={pageSizeOptions}
            selectedPageSize={tableFetchParams.pageSize} // Use unified state
            attributeName={attributeName}
            onChangePage={(pageNo) => {
              // Update unified state
              setTableFetchParams(prev => ({ ...prev, pageIndex: pageNo }));
            }}
            onChangePageSize={(e) => {
              // Update unified state
              setTableFetchParams(prev => ({ ...prev, pageIndex: 0, pageSize: e }));
            }}
            totalRecords={totalRecords}
          />
        )}
        {renderLoader()}
      </Col>
    </Row>
  );
};

export default SwimSwitchServerSideReactTable;
